import React, { Component } from "react";
import Window from "../../2/Controls/Window";
import Input from "../../2/Controls/Input";
import Icon from "../../2/Controls/Icon";
import DiscardDialog from "./DiscardDialog";
import { readImageFile, shrinkImage } from "./templateUtils";

const MAX_WIDTH = 400;
const MAX_HEIGHT = 160;

class SignatureSheet extends Component {
  constructor(props) {
    super(props);
    const signature = props.signature || {};
    this.state = {
      label: signature.label || "Authorized Signature",
      name: signature.name || "",
      image: signature.image || "",
      drawing: false,
      hasInk: false,
      showDiscard: false,
      error: ""
    };
    this.canvas = React.createRef();
    this.pen = null;
  }

  componentDidUpdate(prevProps, prevState) {
    if (this.state.drawing && !prevState.drawing) {
      const canvas = this.canvas.current;
      const ratio = window.devicePixelRatio || 1;
      canvas.width = canvas.offsetWidth * ratio;
      canvas.height = canvas.offsetHeight * ratio;
      const context = canvas.getContext("2d");
      context.scale(ratio, ratio);
      context.lineWidth = 2;
      context.lineCap = "round";
      context.lineJoin = "round";
      context.strokeStyle = "#111827";
    }
  }

  isDirty() {
    const signature = this.props.signature || {};
    return (
      this.state.label !== (signature.label || "Authorized Signature") ||
      this.state.name !== (signature.name || "") ||
      this.state.image !== (signature.image || "")
    );
  }

  onClose = () => {
    if (this.isDirty()) {
      this.setState({ showDiscard: true });
    } else {
      this.props.onClose();
    }
  };

  onButtonClick = (button) => {
    if (button === "Remove") {
      this.props.onSave(null);
      return;
    }
    if (!this.state.image && this.state.name.trim() === "") {
      this.setState({ error: "Please draw or upload a signature, or enter a name" });
      return;
    }
    this.props.onSave({
      label: this.state.label,
      name: this.state.name,
      image: this.state.image
    });
  };

  pointAt(e) {
    const box = this.canvas.current.getBoundingClientRect();
    return { x: e.clientX - box.left, y: e.clientY - box.top };
  }

  onPenDown = (e) => {
    this.pen = this.pointAt(e);
    e.target.setPointerCapture(e.pointerId);
  };

  onPenMove = (e) => {
    if (!this.pen) return;
    const point = this.pointAt(e);
    const context = this.canvas.current.getContext("2d");
    context.beginPath();
    context.moveTo(this.pen.x, this.pen.y);
    context.lineTo(point.x, point.y);
    context.stroke();
    this.pen = point;
    if (!this.state.hasInk) this.setState({ hasInk: true });
  };

  onPenUp = () => {
    this.pen = null;
  };

  clearCanvas = () => {
    const canvas = this.canvas.current;
    canvas.getContext("2d").clearRect(0, 0, canvas.width, canvas.height);
    this.setState({ hasInk: false });
  };

  finishDrawing = () => {
    const canvas = this.canvas.current;
    const image = this.state.hasInk
      ? shrinkImage(canvas, MAX_WIDTH, MAX_HEIGHT)
      : this.state.image;
    this.setState({ image, drawing: false, hasInk: false, error: "" });
  };

  onFileSelected = (e) => {
    const file = e.target.files[0];
    e.target.value = "";
    if (!file) return;
    readImageFile(file, MAX_WIDTH, MAX_HEIGHT)
      .then((image) => this.setState({ image, error: "" }))
      .catch(() => this.setState({ error: "Please choose an image file" }));
  };

  renderSignaturePicker() {
    if (this.state.drawing) {
      return (
        <React.Fragment>
          <canvas
            ref={this.canvas}
            className="pt-sign-canvas"
            onPointerDown={this.onPenDown}
            onPointerMove={this.onPenMove}
            onPointerUp={this.onPenUp}
            onPointerCancel={this.onPenUp}
          />
          <div className="pt-sign-canvas-actions">
            <button type="button" onClick={this.clearCanvas}>Clear</button>
            <button type="button" onClick={this.finishDrawing}>Done</button>
          </div>
        </React.Fragment>
      );
    }

    return (
      <React.Fragment>
        {this.state.image && (
          <div className="pt-sign-current">
            <img src={this.state.image} alt="Signature" />
            <button type="button" onClick={() => this.setState({ image: "" })}>
              Clear
            </button>
          </div>
        )}
        <div className="pt-sign-tiles">
          <button
            type="button"
            className="pt-sign-tile"
            onClick={() => this.setState({ drawing: true })}
          >
            <Icon type="edit" />
            Draw
          </button>
          <label className="pt-sign-tile">
            <Icon type="add" />
            Upload
            <input type="file" accept="image/*" onChange={this.onFileSelected} />
          </label>
        </div>
      </React.Fragment>
    );
  }

  render() {
    return (
      <React.Fragment>
        <Window
          header="Edit Signature"
          modal={true}
          onClose={this.onClose}
          onButtonClick={this.onButtonClick}
          buttons={[
            ...(this.props.signature
              ? [{ Type: "secondary", Text: "Remove", Align: "left" }]
              : []),
            { Type: "primary", Text: "Save", Align: "right" }
          ]}
        >
          <Input
            type="text"
            label="Label"
            value={this.state.label}
            onChange={(label) => this.setState({ label })}
          />
          <Input
            type="text"
            label="Name"
            placeholder="Enter the signature holder's name or designation"
            value={this.state.name}
            onChange={(name) => this.setState({ name, error: "" })}
          />
          <label className="pt-sign-label">Signature</label>
          {this.renderSignaturePicker()}
          {this.state.error && <div className="pt-error">{this.state.error}</div>}
        </Window>
        {this.state.showDiscard && (
          <DiscardDialog
            onStay={() => this.setState({ showDiscard: false })}
            onLeave={this.props.onClose}
          />
        )}
      </React.Fragment>
    );
  }
}

export default SignatureSheet;
